import { CognitoUser } from 'amazon-cognito-identity-js'
import { mapValues } from 'lodash'

export const passwordRules = {
  specialChar: /[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`]/,
  upperCase: /[A-Z]/,
  lowerCase: /[a-z]/,
  digit: /[0-9]/,
  minLength: /.{12,}/,
}

export interface LoginSuccessResult {
  type: 'success'
}

export interface LoginNewPasswordRequired {
  type: 'newPasswordRequired'
  newPasswordAttributes: unknown
  user: CognitoUser
}

export async function completeNewPasswordChallenge(args: {
  cognitoUser: CognitoUser
  newPassword: string
  onCognitoUserChanged: (user: CognitoUser) => void
}): Promise<void> {
  return new Promise((resolve, reject) => {
    args.cognitoUser.completeNewPasswordChallenge(args.newPassword, undefined, {
      onFailure: reject,
      onSuccess: () => {
        args.onCognitoUserChanged(args.cognitoUser)
        resolve()
      },
    })
  })
}

export function signOut(args: {
  cognitoUser: CognitoUser
  onCognitoUserChanged: (user: null) => void
}): void {
  args.cognitoUser.signOut()
  args.onCognitoUserChanged(null)
}

export async function globalSignOut(args: {
  cognitoUser: CognitoUser
  onCognitoUserChanged: (user: null) => void
}): Promise<void> {
  await new Promise((onSuccess, onFailure) => {
    args.cognitoUser.globalSignOut({ onSuccess, onFailure })
  })

  args.onCognitoUserChanged(null)
}

export async function forgotPassword(args: {
  cognitoUser: CognitoUser
}): Promise<void> {
  return new Promise((resolve, reject) => {
    args.cognitoUser.forgotPassword({
      onSuccess: resolve,
      onFailure: reject,
    })
  })
}

export async function confirmPassword(args: {
  cognitoUser: CognitoUser
  verificationCode: string
  newPassword: string
}): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    args.cognitoUser.confirmPassword(args.verificationCode, args.newPassword, {
      onSuccess: (_success: string) => resolve(),
      onFailure: reject,
    })
  })
}

export async function changePassword(args: {
  cognitoUser: CognitoUser
  oldPassword: string
  newPassword: string
}): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    args.cognitoUser.changePassword(
      args.oldPassword,
      args.newPassword,
      (error) => {
        if (error) {
          reject(error)
        } else {
          resolve()
        }
      },
    )
  })
}

export type PasswordValidationResult = {
  [P in keyof typeof passwordRules]: boolean
}

export function validatePassword(password: string): PasswordValidationResult {
  return mapValues(passwordRules, (regex) => regex.test(password))
}
